import {
  Component,
  computed,
  EventEmitter, OnInit,
  Output,
  QueryList,
  signal,
  ViewChildren,
} from "@angular/core";
import {CommonModule, formatDate} from "@angular/common";
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {
  MatChipListboxChange,
  MatChipOption,
  MatChipsModule,
} from "@angular/material/chips";
import {minutesToHHmm} from "../../helpers/dateTime/convertTime";
import {toSignal} from "@angular/core/rxjs-interop";
import {debounceTime, distinctUntilChanged, map} from "rxjs";

interface FilterControl {
  type: string;
  formControls: { [key: string]: any };
  showChip: boolean;
  displayText: string;
}

export type FilterInput = string | null | undefined;

export interface FilterParams {
  startDate?: FilterInput;
  endDate?: FilterInput;
  startTime?: FilterInput;
  endTime?: FilterInput;
  minDuration?: FilterInput;
  maxDuration?: FilterInput;
  duration?: FilterInput;
}

@Component({
  selector: "app-filter-interface",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
  ],
  templateUrl: "./filter-interface.component.html",
  styleUrls: ["./filter-interface.component.scss"],
})
export class FilterInterfaceComponent implements OnInit {
  @Output() closeFilterInterface = new EventEmitter<void>();
  @Output() filterValueChange = new EventEmitter<FilterParams>();
  @ViewChildren(MatChipOption) chipOptions!: QueryList<MatChipOption>;

  // TODO: submit button or trigger filters live?
  filterParamsForm = new FormGroup({
    startDate: new FormControl(""),
    endDate: new FormControl(""),
    startTime: new FormControl(""),
    endTime: new FormControl(""),
    minDuration: new FormControl(""),
    maxDuration: new FormControl(""),
    duration: new FormControl(""),
  });
  filterParams$ = this.filterParamsForm.valueChanges
    .pipe(debounceTime(300), distinctUntilChanged())
    .subscribe((filterParams) => {
      localStorage.setItem('filterParams', JSON.stringify(filterParams));
      this.filterValueChange.emit(filterParams)
    });

  readonly formValues = toSignal(
    this.filterParamsForm.valueChanges.pipe(debounceTime(300))
  );

  readonly presetFilters = signal([
    {name: "> 5 min", value: 5},
    {name: "> 30 min", value: 30},
    {name: "> 1 h", value: 60},
  ]);
  readonly filters = computed(() => [
    {
      type: "dateFilter",
      formControls: {
        startDate: this.filterParamsForm.controls.startDate,
        endDate: this.filterParamsForm.controls.endDate,
      },
      showChip: !!(this.formValues()?.startDate && this.formValues()?.endDate),
      displayText:
        this.formValues()?.startDate && this.formValues()?.endDate
          ? `${formatDate(
            this.formValues()?.startDate as string,
            "dd.MM.yyyy",
            "de"
          )} - ${formatDate(
            this.formValues()?.endDate as string,
            "dd.MM.yyyy",
            "de"
          )}`
          : "",
    },
    {
      type: "timeFilter",
      formControls: {
        startTime: this.filterParamsForm.controls.startTime,
        endTime: this.filterParamsForm.controls.endTime,
      },
      showChip: !!(this.formValues()?.startTime && this.formValues()?.endTime),
      displayText: `${this.formValues()?.startTime} - ${
        this.formValues()?.endTime
      } Uhr`,
    },
    {
      type: "durationFilter",
      formControls: {
        minDuration: this.filterParamsForm.controls.minDuration,
        maxDuration: this.filterParamsForm.controls.maxDuration,
      },
      showChip:
        !!(this.formValues()?.minDuration || this.formValues()?.maxDuration),
      displayText: this.formValues()?.maxDuration
        ? this.formValues()?.minDuration
          ? `${this.formValues()?.minDuration} - ${
            this.formValues()?.maxDuration
          }`
          : `< ${this.formValues()?.maxDuration}`
        : `> ${this.formValues()?.minDuration}`,
    },
  ]);

  activeFilters = computed(() =>
    this.filters().filter((filter) => filter.showChip)
  );

  ngOnInit() {
    const filterParams = JSON.parse(localStorage.getItem('filterParams') || '{}');
    this.filterParamsForm.patchValue(filterParams);
  }

  logSelection(event: MatChipListboxChange) {
    this.filterParamsForm.patchValue({
      minDuration: event.value ? minutesToHHmm(event.value) : null,
    });
  }

  onClick() {
    this.closeFilterInterface.emit();
  }

  resetFilter() {
    this.filterParamsForm.reset();
    this.resetChipSelection();
  }

  removeFilter(filter: FilterControl) {
    const patchObject: { [key: string]: string } = {};
    Object.keys(filter.formControls).forEach((key) => {
      patchObject[key] = "";
    });
    this.filterParamsForm.patchValue(patchObject);
    filter.showChip = false;
    if ((filter.type = "durationFilter")) {
      this.resetChipSelection();
    }
  }

  resetChipSelection() {
    this.chipOptions.forEach((chip) => {
      chip.deselect;
      chip.selected = false;
    });
  }
}
